<script setup>
import MobileQuestionRenderer from '@/views/survey/mobile-questions/MobileQuestionRenderer.vue'
import { VueSignaturePad } from 'vue-signature-pad'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs'
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.mjs?url'
import { nextTick, ref } from 'vue'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

const route = useRoute()
const token = route.params.id || ''

provide('ecoaToken', token)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

// ─── 상태 ───
const step = ref('verify') // 'verify' | 'list' | 'survey'
const error = ref('')
const loading = ref(false)
const submitting = ref(false)

// 인증
const patientId = ref(['', '', '', '', '', ''])

// 목록
const subjectInfo = ref(null)
const schedules = ref([])
const currentDay = ref(null)
const consent = ref({ exists: false, signed: false })

// 설문
const selectedDay = ref(null)
const selectedDayTimeKey = ref(null)
const isReadonly = ref(false)
const surveyPages = ref([])
const answers = ref({}) // { 'groupId-sectionId-questionId': { answer: ... } }
const questionRefs = ref({})

// 동의서
const consentData = ref(null)
const consentGuardianName = ref('')
const consentSignaturePad = ref(null)
const consentSigning = ref(false)
const consentPdfUrl = ref('')
const consentCanvasContainer = ref(null)
const consentRenderFailed = ref(false)
const consentPdfReady = ref(false)

async function renderConsentPdf(arrayBuffer) {
  consentRenderFailed.value = false
  consentPdfReady.value = false
  try {
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
    const pdf = await loadingTask.promise

    // 컨테이너가 DOM에 나타날 때까지 대기 (최대 2초)
    let container = null
    for (let wait = 0; wait < 20; wait++) {
      await nextTick()
      container = consentCanvasContainer.value
      if (container) break
      await new Promise(r => setTimeout(r, 100))
    }
    if (!container) throw new Error('canvas container not mounted')
    container.innerHTML = ''

    // iOS Safari 캔버스 면적 제한 대응 (구형 기기 기준 안전값)
    const MAX_CANVAS_AREA = 8 * 1024 * 1024 // 8M px
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const containerWidth = container.clientWidth || 360

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const base = page.getViewport({ scale: 1 })
      let scale = (containerWidth / base.width) * dpr
      const area = base.width * scale * base.height * scale
      if (area > MAX_CANVAS_AREA) {
        scale *= Math.sqrt(MAX_CANVAS_AREA / area)
      }

      const viewport = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      canvas.className = 'consent-canvas'
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      canvas.style.width = '100%'
      canvas.style.height = 'auto'
      container.appendChild(canvas)
      const ctx = canvas.getContext('2d')
      await page.render({ canvasContext: ctx, viewport }).promise
    }
    consentPdfReady.value = true
  } catch (e) {
    console.error('동의서 PDF 렌더링 실패:', e)
    consentRenderFailed.value = true
  }
}

const storageBaseUrl = import.meta.env.VITE_STORAGE_BASE_URL
  || (import.meta.env.VITE_API_BASE_URL || '').replace(/\/api\/?$/, '/storage')

// ─── 1단계: 인증 ───
const focusNext = (e, index) => {
  if (e.target.value && index < 5) {
    nextTick(() => {
      document.querySelector(`#input-${index + 1}`)?.focus()
    })
  }
}

async function verifyAndLoadList() {
  error.value = ''
  loading.value = true

  try {
    const res = await $api(`/ecoa/resolve/${token}`, { method: 'GET' })

    if (!res || res.error) {
      error.value = res?.error || '인증에 실패했습니다.'
      loading.value = false
      return
    }

    subjectInfo.value = res.subject
    schedules.value = (res.schedules || []).sort((a, b) => {
      if (a.date && b.date && a.date !== b.date) return a.date.localeCompare(b.date)
      if (a.day !== b.day) return a.day - b.day
      return (a.time || '').localeCompare(b.time || '')
    })
    currentDay.value = res.currentDay
    consent.value = res.consent || { exists: false, signed: false }

    // 식별번호 자동 세팅
    if (res.subject?.subject_no) {
      const no = res.subject.subject_no
      const chars = no.split('')
      patientId.value = chars.concat(Array(6 - chars.length).fill('')).slice(0, 6)
    }

    step.value = 'list'
  } catch (e) {
    error.value = '유효하지 않은 링크입니다.'
  } finally {
    loading.value = false
  }
}

// 토큰 검증 결과 보관 (식별번호 대조용)
const resolvedSubjectNo = ref(null)

// 토큰이 있어도 식별번호 인증을 먼저 거쳐야 함
onMounted(async () => {
  if (!token) return
  // 토큰으로 subject_no만 미리 가져옴 (목록은 아직 안 보여줌)
  try {
    const res = await $api(`/ecoa/resolve/${token}`, { method: 'GET' })
    if (res?.subject?.subject_no) {
      resolvedSubjectNo.value = res.subject.subject_no
    }
  } catch {}
})

// 식별번호 입력 후 인증
function manualVerify() {
  const input = 'S' + patientId.value.slice(1).join('')
  if (input.length < 2) {
    error.value = '식별번호를 입력해주세요.'
    return
  }
  if (!resolvedSubjectNo.value) {
    error.value = '유효하지 않은 링크입니다.'
    return
  }
  if (input !== resolvedSubjectNo.value) {
    error.value = '식별번호가 일치하지 않습니다.'
    return
  }
  verifyAndLoadList()
}

// ─── 2단계: 목록에서 Day 선택 ───
async function openSurvey(dayItem) {
  loading.value = true
  selectedDay.value = dayItem.day
  selectedDayTimeKey.value = dayItem.dayTimeKey
  isReadonly.value = !!dayItem.completed
  error.value = ''

  try {
    const res = await $api(`/ecoa/page/${token}/${dayItem.dayTimeKey || dayItem.day}`, { method: 'GET' })

    if (!res || res.error) {
      error.value = res?.error || '설문을 불러올 수 없습니다.'
      loading.value = false
      return
    }

    surveyPages.value = res.pages || []

    // 기존 답변이 있으면 로드
    answers.value = res.existingAnswer || {}

    questionRefs.value = {}
    step.value = 'survey'
  } catch (e) {
    error.value = '설문 데이터를 불러오는 중 오류가 발생했습니다. (' + (e?.message || e) + ')'
  } finally {
    loading.value = false
  }
}

function backToList() {
  step.value = 'list'
  surveyPages.value = []
  answers.value = {}
}

// ─── 동의서 ───
async function openConsent() {
  loading.value = true
  error.value = ''
  consentPdfUrl.value = ''
  consentRenderFailed.value = false
  consentPdfReady.value = false
  try {
    const res = await $api(`/ecoa/consent/${token}`, { method: 'GET' })
    consentData.value = res
    consentGuardianName.value = res.existingSignature?.guardianName || ''

    step.value = 'consent'
    loading.value = false

    // PDF를 canvas에 렌더링
    if (res.pdfPath) {
      consentPdfUrl.value = storageBaseUrl + '/' + res.pdfPath
      await nextTick()
      try {
        const pdfRes = await fetch(consentPdfUrl.value)
        if (!pdfRes.ok) throw new Error(`PDF fetch ${pdfRes.status}`)
        const ab = await pdfRes.arrayBuffer()
        await renderConsentPdf(ab)
      } catch (e) {
        console.error('동의서 PDF 로드 실패:', e)
        consentRenderFailed.value = true
      }
    }
  } catch (e) {
    error.value = '동의서를 불러올 수 없습니다.'
    loading.value = false
  }
}

async function submitConsent() {
  const fields = consentData.value?.fields || []
  const hasNameField = fields.some(f => f.type === 'guardian_name')
  const hasSignatureField = fields.some(f => f.type === 'signature')

  if (hasNameField && !consentGuardianName.value.trim()) {
    error.value = '보호자 이름을 입력해주세요.'
    return
  }
  if (hasSignatureField && (!consentSignaturePad.value || consentSignaturePad.value.isEmpty())) {
    error.value = '서명을 해주세요.'
    return
  }

  consentSigning.value = true
  error.value = ''
  try {
    const sigData = hasSignatureField && consentSignaturePad.value
      ? consentSignaturePad.value.saveSignature().data
      : ''
    await $api(`/ecoa/consent/${token}`, {
      method: 'POST',
      body: {
        consentFormId: consentData.value.consentFormId,
        guardianName: consentGuardianName.value,
        signatureData: sigData,
      },
    })
    await verifyAndLoadList()
    step.value = 'list'
    alert('동의서가 제출되었습니다. 감사합니다.')
  } catch (e) {
    error.value = '동의서 제출에 실패했습니다.'
  } finally {
    consentSigning.value = false
  }
}

function clearConsentSignature() {
  consentSignaturePad.value?.clearSignature()
}

// ─── 3단계: 설문 제출 ───
function getAnswerKey(groupId, sectionId, questionId) {
  return `${groupId}-${sectionId}-${questionId}`
}

async function submitSurvey() {
  // 필수 항목 검증
  const invalidRefs = Object.values(questionRefs.value).filter(r => r && !r.isValid)
  if (invalidRefs.length > 0) {
    error.value = '필수 항목을 모두 입력해주세요.'
    return
  }

  submitting.value = true
  error.value = ''

  // 모든 답변 수집: ref 키(=answer key)로 getAnswer() 결과를 매핑
  const collectedAnswers = {}
  Object.entries(questionRefs.value).forEach(([key, r]) => {
    if (r?.getAnswer) {
      collectedAnswers[key] = r.getAnswer()
    }
  })

  // answers ref 위에 getAnswer() 결과를 덮어써서 최종 상태 반영
  const finalAnswers = { ...answers.value, ...collectedAnswers }

  try {
    const res = await $api(`/ecoa/answer/${token}`, {
      method: 'POST',
      body: {
        day: selectedDay.value,
        dayTimeKey: selectedDayTimeKey.value,
        answers: finalAnswers,
      },
    })

    if (res?.status === 'ok') {
      // 목록으로 돌아가면서 완료 상태 갱신
      await verifyAndLoadList()
      step.value = 'list'
      alert('설문이 제출되었습니다. 감사합니다.')
    } else {
      error.value = res?.error || '제출에 실패했습니다.'
    }
  } catch (e) {
    error.value = '제출 중 오류가 발생했습니다.'
  } finally {
    submitting.value = false
  }
}

function setQuestionRef(el, key) {
  if (el) questionRefs.value[key] = el
  else delete questionRefs.value[key]
}
</script>

<template>
  <div class="mobile-wrapper">
    <!-- ═══ 1단계: 인증 ═══ -->
    <template v-if="step === 'verify'">
      <header class="mobile-header">
        <h2>보호자 설문</h2>
      </header>

      <main class="form-section">
        <h3 class="form-title">
          보호자 설문 수집 시스템
        </h3>
        <p class="form-description">
          문자메시지로 전달받은 링크를 통해 접속하셨습니다.<br>
          잠시 후 설문 목록이 표시됩니다.
        </p>

        <div
          v-if="loading"
          class="text-center py-8"
        >
          <VProgressCircular indeterminate color="primary" />
          <p class="mt-4">
            인증 중...
          </p>
        </div>

        <div v-if="!loading" class="input-box">
          <p class="form-description">
            문자메시지(SMS)로 동물병원에서 전달받은 환자 ID를 입력해주세요.
          </p>
          <div class="id-box">
            <span class="id-prefix">S</span>
            <template
              v-for="(box, index) in patientId.slice(1)"
              :key="index"
            >
              <input
                :id="`input-${index + 1}`"
                v-model="patientId[index + 1]"
                maxlength="1"
                class="id-input"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                :autofocus="index === 0"
                @input="(e) => focusNext(e, index + 1)"
              >
            </template>
          </div>
          <div class="reset-btn-wrapper">
            <button class="reset-btn" @click="patientId = ['','','','','','']">
              입력값 초기화
            </button>
          </div>
        </div>

        <p v-if="error" class="error-text">
          {{ error }}
        </p>
      </main>

      <footer class="mobile-footer">
        <VBtn block color="primary" :loading="loading" @click="manualVerify">
          다음
        </VBtn>
      </footer>
    </template>

    <!-- ═══ 2단계: 스케줄 목록 ═══ -->
    <template v-if="step === 'list'">
      <header class="mobile-header">
        <h2>설문 목록</h2>
        <p class="header-sub">
          {{ subjectInfo?.name }} ({{ subjectInfo?.subject_no }})
        </p>
      </header>

      <main class="form-section">
        <!-- 보호자 동의서 (최상위 고정) -->
        <div v-if="consent.exists" class="schedule-list mb-3">
          <div
            class="schedule-item"
            :class="{ completed: consent.signed }"
            @click="openConsent()"
          >
            <div class="schedule-left">
              <div class="day-badge" style="background: #e8f5e9; color: #2e7d32;">
                동의서
              </div>
              <div class="schedule-date">보호자 동의서</div>
            </div>
            <div class="schedule-right">
              <VChip
                :color="consent.signed ? 'success' : 'warning'"
                size="small"
                variant="tonal"
              >
                {{ consent.signed ? '서명완료' : '서명필요' }}
              </VChip>
              <VIcon icon="tabler-chevron-right" size="20" color="grey" />
            </div>
          </div>
        </div>

        <div v-if="schedules.length === 0 && !consent.exists" class="text-center py-8 text-medium-emphasis">
          현재 작성할 수 있는 설문이 없습니다.
        </div>

        <div v-if="schedules.length === 0 && consent.exists" class="text-center py-4 text-medium-emphasis" style="font-size: 14px;">
          위의 동의서를 작성해주세요.
        </div>

        <div v-if="schedules.length > 0" class="schedule-list">
          <div
            v-for="(item, idx) in schedules"
            :key="item.dayTimeKey || idx"
            class="schedule-item"
            :class="{ completed: item.completed }"
            @click="openSurvey(item)"
          >
            <div class="schedule-left">
              <div class="day-badge" :class="{ 'day-current': item.day === currentDay }">
                Day {{ item.day }}
              </div>
              <div class="schedule-date">
                {{ item.date || '날짜 미정' }} {{ item.time }}
              </div>
            </div>
            <div class="schedule-right">
              <VChip
                :color="item.completed ? 'success' : 'warning'"
                size="small"
                variant="tonal"
              >
                {{ item.completed ? '완료' : '미완료' }}
              </VChip>
              <VIcon icon="tabler-chevron-right" size="20" color="grey" />
            </div>
          </div>
        </div>

        <p v-if="error" class="error-text mt-4">
          {{ error }}
        </p>
      </main>
    </template>

    <!-- ═══ 3단계: eCOA 설문 작성 ═══ -->
    <template v-if="step === 'survey'">
      <header class="mobile-header">
        <div class="d-flex align-center justify-space-between">
          <VBtn variant="text" size="small" @click="backToList">
            <VIcon icon="tabler-arrow-left" /> 목록
          </VBtn>
          <h2>Day {{ selectedDay }} 설문</h2>
          <div style="inline-size: 60px;" />
        </div>
      </header>

      <main class="form-section">
        <div v-if="loading" class="text-center py-8">
          <VProgressCircular indeterminate color="primary" />
        </div>

        <template v-else>
          <div :class="{ 'readonly-overlay': isReadonly }">
          <div
            v-for="page in surveyPages"
            :key="page.groupId"
            class="survey-group"
          >
            <h3 v-if="page.groupLabel" class="group-title">
              {{ page.groupLabel }}
            </h3>

            <div
              v-for="section in page.sections"
              :key="section.id"
              class="survey-section"
            >
              <h4 v-if="section.title" class="section-title">
                {{ section.sectionNumber }} {{ section.title }}
              </h4>

              <div
                v-for="(question, qIdx) in (section.questions || [])"
                :key="question.questionId || qIdx"
                class="question-wrap"
              >
                <MobileQuestionRenderer
                  :ref="el => setQuestionRef(el, getAnswerKey(page.groupId, section.id, question.questionId))"
                  :question-data="question"
                  :model-value="answers[getAnswerKey(page.groupId, section.id, question.questionId)] || {}"
                  @update:model-value="val => answers[getAnswerKey(page.groupId, section.id, question.questionId)] = val"
                />
              </div>
            </div>
          </div>
          </div>
        </template>

        <p v-if="error" class="error-text mt-4">
          {{ error }}
        </p>
      </main>

      <footer v-if="!isReadonly" class="mobile-footer">
        <VBtn
          block
          color="primary"
          :loading="submitting"
          @click="submitSurvey"
        >
          제출하기
        </VBtn>
      </footer>
    </template>

    <!-- ═══ 동의서 서명 ═══ -->
    <template v-if="step === 'consent'">
      <header class="mobile-header">
        <div class="d-flex align-center justify-space-between">
          <VBtn variant="text" size="small" @click="backToList">
            <VIcon icon="tabler-arrow-left" /> 목록
          </VBtn>
          <h2>보호자 동의서</h2>
          <div style="inline-size: 60px;" />
        </div>
      </header>

      <main class="form-section">
        <div v-if="loading" class="text-center py-8">
          <VProgressCircular indeterminate color="primary" />
        </div>

        <template v-else-if="consentData">
          <!-- 동의서 PDF 미리보기 -->
          <div v-if="consentPdfUrl" class="consent-pdf mb-4">
            <div class="d-flex justify-space-between align-center mb-2">
              <span class="text-subtitle-2">동의서 내용</span>
              <a
                :href="consentPdfUrl"
                target="_blank"
                rel="noopener"
                class="text-caption"
              >새 창에서 열기</a>
            </div>
            <div
              v-show="!consentRenderFailed"
              ref="consentCanvasContainer"
              class="consent-canvas-area"
            />
            <div v-if="consentRenderFailed" class="consent-fallback">
              <p class="text-body-2 text-medium-emphasis mb-3">
                이 기기에서는 미리보기를 표시할 수 없습니다.<br>
                아래 버튼을 눌러 동의서를 확인해주세요.
              </p>
              <VBtn
                :href="consentPdfUrl"
                target="_blank"
                rel="noopener"
                color="primary"
                variant="outlined"
                block
              >
                동의서 PDF 열기
              </VBtn>
            </div>
          </div>

          <!-- 자동 필드 표시 -->
          <div class="consent-info mb-4">
            <div v-for="field in (consentData.fields || []).filter(f => f.auto)" :key="field.type" class="info-row">
              <span class="info-label">{{ field.label }}</span>
              <span class="info-value">{{ consentData.autoValues?.[field.type] || '-' }}</span>
            </div>
          </div>

          <!-- 보호자 이름 입력 (등록된 필드일 때만 표시) -->
          <div v-if="(consentData.fields || []).some(f => f.type === 'guardian_name')" class="mb-4">
            <VTextField
              v-model="consentGuardianName"
              label="보호자 이름"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              :readonly="consent.signed"
            />
          </div>

          <!-- 서명 (등록된 필드일 때만 표시) -->
          <template v-if="(consentData.fields || []).some(f => f.type === 'signature')">
            <div v-if="!consent.signed" class="mb-4">
              <div class="text-subtitle-2 mb-2">서명</div>
              <div class="signature-area">
                <VueSignaturePad
                  ref="consentSignaturePad"
                  width="100%"
                  height="200px"
                  :options="{ penColor: 'black', minWidth: 1.5, maxWidth: 3 }"
                  class="signature-pad-consent"
                />
              </div>
              <VBtn size="small" variant="text" color="error" class="mt-1" @click="clearConsentSignature">
                서명 지우기
              </VBtn>
            </div>
            <div v-else class="mb-4 text-center text-medium-emphasis">
              서명 완료된 동의서입니다.
            </div>
          </template>

          <p v-if="error" class="error-text mt-2">{{ error }}</p>
        </template>
      </main>

      <footer v-if="!consent.signed" class="mobile-footer">
        <VBtn
          block
          color="primary"
          :loading="consentSigning"
          @click="submitConsent"
        >
          동의서 제출
        </VBtn>
      </footer>
    </template>
  </div>
</template>

<style scoped>
.mobile-wrapper {
  display: flex;
  flex-direction: column;
  margin: auto;
  background-color: #f9f9f9;
  font-size: 16px;
  max-inline-size: 480px;
  min-block-size: 100vh;
}

.mobile-header {
  position: sticky;
  z-index: 10;
  padding: 1rem;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 6%);
  font-size: 1.1rem;
  font-weight: bold;
  text-align: center;
  inset-block-start: 0;
}

.header-sub { color: #666; font-size: 0.85rem; font-weight: 400; margin-block-start: 4px; }

.form-section { flex: 1; padding: 1rem; }
.form-title { font-size: 1.1rem; margin-block-end: 0.5rem; }
.form-description { color: #666; font-size: 0.9rem; line-height: 1.5; margin-block-end: 1rem; }

.id-box { display: flex; align-items: center; justify-content: center; gap: 6px; margin-block-end: 1rem; }
.id-input { border: 1px solid #ccc; border-radius: 4px; block-size: 2rem; font-size: 18px; inline-size: 2rem; text-align: center; }
.id-prefix { border-radius: 4px; background-color: #eee; font-size: 18px; font-weight: bold; padding-block: 0.25rem; padding-inline: 0.75rem; }
.reset-btn-wrapper { margin-block-end: 1rem; text-align: center; }
.reset-btn { border: 1px solid #ccc; border-radius: 4px; background: #fff; cursor: pointer; font-size: 0.85rem; padding-block: 4px; padding-inline: 12px; }
.error-text { color: red; font-size: 0.85rem; text-align: center; }

/* 스케줄 목록 */
.schedule-list { display: flex; flex-direction: column; gap: 8px; }
.schedule-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border: 1px solid #e8ebee; border-radius: 12px;
  background: #fff; cursor: pointer; transition: background 0.15s;
}
.schedule-item:hover { background: #f8faff; }
.schedule-item.completed { opacity: 0.7; }
.schedule-left { display: flex; align-items: center; gap: 12px; }
.schedule-right { display: flex; align-items: center; gap: 8px; }
.day-badge {
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px; background: #f0f2ff; color: #4a5bc7;
  font-size: 13px; font-weight: 700; padding: 4px 12px;
}
.day-badge.day-current { background: #4a5bc7; color: #fff; }
.schedule-date { color: #555; font-size: 14px; }

/* 설문 */
.survey-group { margin-block-end: 16px; }
.group-title { color: #333; font-size: 1rem; font-weight: 700; margin-block-end: 12px; padding-block-end: 8px; border-block-end: 2px solid #4a5bc7; }
.survey-section { margin-block-end: 16px; padding: 12px; border: 1px solid #e8ebee; border-radius: 12px; background: #fff; }
.section-title { color: #4a5bc7; font-size: 0.9rem; font-weight: 600; margin-block-end: 12px; }
.question-wrap { margin-block-end: 16px; padding-block-end: 12px; border-block-end: 1px solid #f0f2f5; }
.question-wrap:last-child { margin-block-end: 0; padding-block-end: 0; border-block-end: none; }

.mobile-footer {
  position: sticky; padding: 1rem; background-color: #fff;
  box-shadow: 0 -1px 5px rgba(0, 0, 0, 5%); inset-block-end: 0;
}

.readonly-overlay { pointer-events: none; opacity: 0.75; }

/* 동의서 */
.consent-info { padding: 12px; background: #f8f9fc; border-radius: 10px; }
.info-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #eee; }
.info-row:last-child { border-bottom: none; }
.info-label { color: #666; font-size: 13px; }
.info-value { font-weight: 600; font-size: 14px; }
.consent-canvas-area { overflow: auto; max-block-size: 50vh; border: 1px solid #e0e0e0; border-radius: 8px; background: #f5f5f5; }
.consent-canvas { display: block; max-inline-size: 100%; block-size: auto; }
.consent-fallback { padding: 24px 16px; border: 1px dashed #bdbdbd; border-radius: 8px; background: #fafafa; text-align: center; }
.signature-area { border: 1px solid #ddd; border-radius: 8px; overflow: hidden; touch-action: none; }
.signature-pad-consent { background: #fff; }
</style>
